import { Component, Output, EventEmitter, Input, ViewChild, OnInit, ChangeDetectorRef, ChangeDetectionStrategy, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormGroup, Validators, FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ModalDirective } from "../../directives/modal.directive";
import { AppSettings } from "../../appsettings";
import { ILatestUpdateModal } from "../../interfaces/ilatestupdatemodal";
import { BusinessFunctionService } from "../../services/businessfunction.service";
import { Store } from "@ngrx/store";
import { IAppStore } from "../../iappstore";
import { IBusinessFunction } from "../../interfaces/ibusinessfunction";
import { CKEditorModule } from "@ckeditor/ckeditor5-angular";
import { CkEditorEnhancedDirective } from "../../directives/ck-editor-enhanced.directive";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";


@Component({
    selector: "latestupdatemodal",
    templateUrl: "./latestupdatemodal.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, ModalDirective, CKEditorModule, CkEditorEnhancedDirective],
    schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
    styles: [`
        .modal-body {
            padding: 1rem 1.5rem;
        }
        .modal-body p {
            margin-bottom: 1rem !important;
            padding-left: 0;
        }
        .modal-body label {
            margin-bottom: 0 !important;
        }
        .modal-body input[type="radio"],
        .modal-body input[type="checkbox"] {
            margin-bottom: 0 !important;
        }
        .d-flex.align-items-center {
            margin-bottom: 0.5rem !important;
        }
        .d-flex.align-items-center:last-child {
            margin-bottom: 0 !important;
        }
        .d-flex.align-items-center input[type="radio"],
        .d-flex.align-items-center input[type="checkbox"] {
            margin-top: 0 !important;
            margin-bottom: 0 !important;
            margin-right: 0.5rem !important;
            flex-shrink: 0;
        }
        .d-flex.align-items-center label {
            margin-bottom: 0 !important;
            flex-grow: 1;
        }
        .modal-footer {
            padding: 1rem 1.5rem;
        }
        .modal-footer .d-flex {
            align-items: center;
        }
        .modal-footer input[type="checkbox"] {
            margin-top: 0 !important;
            margin-bottom: 0 !important;
            margin-right: 0.5rem !important;
            flex-shrink: 0;
        }
        .modal-footer label {
            margin-bottom: 0 !important;
            white-space: nowrap;
        }
        .modal-footer .btn {
            flex-shrink: 0;
        }
        /* Fix CKEditor dialog z-index to appear above Bootstrap modal */
        ::ng-deep .ck.ck-balloon-panel {
            z-index: 1055 !important;
        }
        ::ng-deep .ck-dialog-overlay {
            z-index: 1055 !important;
        }
        ::ng-deep .ck.ck-dialog {
            z-index: 1056 !important;
        }
        ::ng-deep .ck.ck-dropdown__panel {
            z-index: 1055 !important;
        }
        ::ng-deep .ck.ck-tooltip {
            z-index: 1060 !important;
        }
        /* Ensure link balloon appears correctly */
        ::ng-deep .ck.ck-link-form {
            z-index: 1055 !important;
        }
        ::ng-deep .ck.ck-link-actions {
            z-index: 1055 !important;
        }

    `]
})
export class LatestUpdateModalComponent implements OnInit {
    @Output() onLatestUpdateUpdated = new EventEmitter();
    @Input() latestUpdate: string = "";
    @Input() allowCorrections: boolean = false;
    latestUpdateForm: FormGroup;
    loaded: boolean = false;
    formInitialized: boolean = false;
    config: any = {};
    public Editor = ClassicEditor;
    @ViewChild("latestUpdateModal") public latestUpdateModal: ModalDirective;
    businessfunctionId: number;
    emailFeatureEnabled: boolean = false;

    constructor(private readonly store: Store<IAppStore>,
        private businessFunctionService: BusinessFunctionService,
        private cdr: ChangeDetectorRef) {
    }

    get showLatestUpdateContent(): boolean {
        return this.latestUpdateForm?.controls?.['latestUpdateAlert']?.value === true;
    }

    get showEmailAlert(): boolean {
        return this.latestUpdateForm?.controls?.['latestUpdateAlert']?.value === true && this.emailFeatureEnabled;
    }

    get isPublishDisabled(): boolean {
        return !this.latestUpdateForm?.valid || this.latestUpdateForm?.controls?.['publishCheck']?.value !== true;
    }

    /**
     * Initializes the modal and sets the validation for the form
     */
    ngOnInit(): void {

        this.store.select(e => e.user).subscribe(e => {
            if (e.selectedBusinessFunction) {
                this.businessfunctionId = e.selectedBusinessFunction.id;
            }
        });

        // Configure CKEditor with explicit settings for modal context
        this.config = {
            toolbar: {
                items: [
                    'heading',
                    '|',
                    'bold',
                    'italic',
                    '|',
                    'link',
                    'bulletedList',
                    'numberedList',
                    '|',
                    'outdent',
                    'indent',
                    '|',
                    'blockQuote',
                    '|',
                    'undo',
                    'redo'
                ],
                shouldNotGroupWhenFull: true
            },
            heading: {
                options: [
                    { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                    { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
                    { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
                    { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
                    { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' }
                ]
            },
            link: {
                decorators: {
                    openInNewTab: {
                        mode: 'manual',
                        label: 'Open in a new tab',
                        attributes: {
                            target: '_blank',
                            rel: 'noopener noreferrer'
                        }
                    }
                }
            },
            // Enable resizable editor with minimum height
            height: '300px',
            // Add balloon toolbar for better link editing experience
            balloonToolbar: ['bold', 'italic', 'link'],
            // Suppress all CKEditor branding including focus state
            licenseKey: '',
            // Critical: Ensure proper z-index behavior in modals
            ui: {
                viewportOffset: {
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0
                },
                // Remove "Powered by CKEditor" branding completely
                poweredBy: {
                    forceVisible: false,
                    label: null,
                    side: 'right'
                }
            }
        };
        this.latestUpdateForm = new FormGroup({
            latestUpdateContent: new FormControl(this.latestUpdate, [Validators.minLength(3), Validators.required]),
            latestUpdateAlert: new FormControl(false),
            emailAlert: new FormControl(false),
            isCorrection: new FormControl("false"),
            publishCheck: new FormControl(false)
        });
    }

    onOpenModal(): void {
        // Reset form values first
        this.latestUpdateForm.patchValue({
            latestUpdateContent: this.latestUpdate,
            latestUpdateAlert: false,
            emailAlert: false,
            isCorrection: "false",
            publishCheck: false
        });

        this.loaded = true;

        // Fix z-index issues for CKEditor in modal and add debugging
        // Use longer delay to ensure CKEditor is fully initialized
        setTimeout(() => {
            this.waitForCKEditorAndSetup();
            this.removeCKEditorBranding();
        }, 1000);

        // Get business function data
        this.businessFunctionService.getBusinessFunction(this.businessfunctionId).subscribe(
            data => {
                data.features.forEach(obj => {
                    if (obj.featureId == 8) {
                        this.emailFeatureEnabled = obj.enabled;
                    }
                });
                // Trigger change detection after async operation
                this.cdr.markForCheck();
            });

        // Mark for check after form reset
        this.cdr.markForCheck();
    }

    /**
     * Emits the change to the caller and closes the modal
     */
    onLatestUpdateSave(): void {

        let email: boolean =
            this.latestUpdateForm.controls["emailAlert"].value && this.latestUpdateForm.controls["latestUpdateAlert"].value;
        this.onLatestUpdateUpdated.emit({
            description: this.latestUpdateForm.controls["latestUpdateContent"].value,
            sendEmail: email,
            latestUpdateAlert: this.latestUpdateForm.controls["latestUpdateAlert"].value,
            isCorrection: this.latestUpdateForm.controls["isCorrection"].value === "true"
        } as ILatestUpdateModal);

        this.latestUpdateModal.hide();
    }

    openModal(): void {
        this.latestUpdateModal.show();
        this.onOpenModal();
    }

    closeModal(): void {
        this.latestUpdateModal.hide();
    }



    private fixCKEditorZIndex(): void {
        // Ensure CKEditor elements appear above Bootstrap modal
        const ckElements = [
            '.ck.ck-balloon-panel',
            '.ck-dialog-overlay',
            '.ck.ck-dialog',
            '.ck.ck-dropdown__panel',
            '.ck.ck-tooltip',
            '.ck.ck-link-form',
            '.ck.ck-link-actions'
        ];

        ckElements.forEach(selector => {
            const elements = document.querySelectorAll(selector);
            elements.forEach((element: HTMLElement) => {
                if (element.style) {
                    element.style.zIndex = '1055';
                }
            });
        });

        // Also add event listener to fix z-index when CKEditor creates new elements
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            const element = node as HTMLElement;
                            if (element.classList && element.classList.contains('ck')) {
                                element.style.zIndex = '1055';
                            }
                        }
                    });
                }
            });
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        // Clean up observer when modal closes
        setTimeout(() => {
            observer.disconnect();
        }, 30000); // 30 seconds cleanup
    }

    private waitForCKEditorAndSetup(): void {
        let attempts = 0;
        const maxAttempts = 20; // Wait up to 10 seconds

        const checkForCKEditor = () => {
            attempts++;
            const ckElements = document.querySelectorAll('.ck-editor');
            const linkButton = document.querySelector('.ck-toolbar .ck-button[title*="Link"]');

            if (ckElements.length > 0 || attempts >= maxAttempts) {
                this.fixCKEditorZIndex();
                this.debugCKEditor();
                this.addCustomLinkHandler();
            } else {
                // Try again in 500ms
                setTimeout(checkForCKEditor, 500);
            }
        };

        checkForCKEditor();
    }

    private debugCKEditor(): void {
        // More comprehensive search for link button
        const linkButtons = [
            document.querySelector('.ck-toolbar .ck-button[title*="Link"]'),
            document.querySelector('.ck-toolbar .ck-button[title*="link"]'),
            document.querySelector('.ck-toolbar .ck-button[data-cke-tooltip-text*="Link"]'),
            document.querySelector('.ck-toolbar .ck-button[aria-label*="Link"]')
        ];

        const linkButton = linkButtons.find(btn => btn !== null);

        if (linkButton) {
            // Add click event listener for proper link dialog positioning
            linkButton.addEventListener('click', (e) => {
                // Single timeout to position the dialog properly
                setTimeout(() => {
                    this.forceShowLinkDialog();
                }, 100);
            });
        }
    }

    private forceShowLinkDialog(): void {
        // Find the main link form/balloon panel AND link actions
        const linkForm = document.querySelector('.ck-link-form') as HTMLElement;
        const linkActions = document.querySelector('.ck-link-actions') as HTMLElement;
        const balloonPanel = document.querySelector('.ck-balloon-panel') as HTMLElement;

        // Hide any duplicate or extra balloons first
        const allBalloons = document.querySelectorAll('.ck-balloon-panel');
        allBalloons.forEach((balloon, index) => {
            const element = balloon as HTMLElement;
            if (index > 0) { // Hide all except the first one
                element.style.display = 'none';
            }
        });

        // Hide ONLY navigation buttons (Previous/Next), keep Save/Cancel buttons
        const navSelectors = [
            '.ck-balloon-rotator__navigation',
            '.ck-balloon-rotator__counter',
            '.ck-button[data-cke-tooltip-text="Previous"]',
            '.ck-button[data-cke-tooltip-text="Next"]',
            '.ck-button[title="Previous"]',
            '.ck-button[title="Next"]'
        ];

        navSelectors.forEach(selector => {
            const elements = document.querySelectorAll(selector);
            elements.forEach(element => {
                const htmlElement = element as HTMLElement;
                htmlElement.style.display = 'none';
                htmlElement.style.visibility = 'hidden';
                htmlElement.style.opacity = '0';
            });
        });        // Style both link form AND link actions (prefer linkForm, then linkActions, then balloonPanel)
        const primaryDialog = linkForm || linkActions || balloonPanel;

        // Also apply styling to link actions specifically if it exists
        if (linkActions) {
            linkActions.style.cssText += `
                z-index: 999999 !important;
                position: absolute !important;
                display: block !important;
                visibility: visible !important;
                opacity: 1 !important;
                background: #fafafa !important;
                border: 1px solid #e0e0e0 !important;
                border-radius: 4px !important;
                box-shadow: 0 2px 10px rgba(0,0,0,0.1) !important;
            `;
        }

        if (primaryDialog) {
            // Apply proper dialog styling with border and clean appearance
            primaryDialog.style.cssText += `
                z-index: 999999 !important;
                position: absolute !important;
                display: block !important;
                visibility: visible !important;
                opacity: 1 !important;
                background: #fafafa !important;
                border: 1px solid #e0e0e0 !important;
                border-radius: 4px !important;
                box-shadow: 0 2px 10px rgba(0,0,0,0.1) !important;
            `;
        }

        // Monitor and hide navigation buttons that might appear after dialog is shown
        setTimeout(() => this.hideNavigationButtons(), 200);
        setTimeout(() => this.hideNavigationButtons(), 500);

        // Also monitor for link actions popup that might appear when clicking existing links
        setTimeout(() => this.ensureLinkActionsVisible(), 200);
        setTimeout(() => this.ensureLinkActionsVisible(), 500);
    }

    private hideNavigationButtons(): void {
        // Only hide navigation buttons, preserve Save/Cancel/etc buttons
        const navSelectors = [
            '.ck-balloon-rotator__navigation',
            '.ck-balloon-rotator__counter',
            '.ck-button[data-cke-tooltip-text="Previous"]',
            '.ck-button[data-cke-tooltip-text="Next"]',
            '.ck-button[title="Previous"]',
            '.ck-button[title="Next"]'
        ];

        navSelectors.forEach(selector => {
            const elements = document.querySelectorAll(selector);
            elements.forEach(element => {
                const htmlElement = element as HTMLElement;
                htmlElement.style.display = 'none';
                htmlElement.style.visibility = 'hidden';
                htmlElement.style.opacity = '0';
            });
        });
    }

    private ensureLinkActionsVisible(): void {
        // Find and ensure link actions popup is visible when clicking existing links
        const linkActions = document.querySelector('.ck-link-actions') as HTMLElement;
        const linkActionsBalloon = document.querySelector('.ck-balloon-panel.ck-link-actions') as HTMLElement;

        [linkActions, linkActionsBalloon].forEach(element => {
            if (element) {
                element.style.cssText += `
                    z-index: 999999 !important;
                    position: absolute !important;
                    display: block !important;
                    visibility: visible !important;
                    opacity: 1 !important;
                    background: #fafafa !important;
                    border: 1px solid #e0e0e0 !important;
                    border-radius: 4px !important;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.1) !important;
                    --ck-color-link-default: #00A3E0 !important;
                `;

                // Also fix any link elements inside the popup
                const linkElements = element.querySelectorAll('a, .ck-link-actions__preview');
                linkElements.forEach(link => {
                    (link as HTMLElement).style.color = '#00A3E0';
                });
            }
        });
    }

    private addCustomLinkHandler(): void {
        // Find the link button and add custom handler if default doesn't work
        const linkButton = document.querySelector('.ck-toolbar .ck-button[title*="Link"]') as HTMLElement;

        if (linkButton) {
            // Remove existing listeners and add our custom one
            const newLinkButton = linkButton.cloneNode(true) as HTMLElement;
            linkButton.parentNode?.replaceChild(newLinkButton, linkButton);

            newLinkButton.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.showCustomLinkDialog();
            });
        }
    }

    private showCustomLinkDialog(): void {
        const url = prompt('Enter the URL:');
        if (url) {
            const linkText = prompt('Enter the link text (or leave empty to use URL):') || url;

            // Insert HTML into CKEditor
            const editorElement = document.querySelector('.ck-editor .ck-editor__editable') as HTMLElement;
            if (editorElement) {
                // Focus the editor first
                editorElement.focus();

                // Get current selection
                const selection = window.getSelection();
                if (selection && selection.rangeCount > 0) {
                    const range = selection.getRangeAt(0);

                    // Create link element
                    const link = document.createElement('a');
                    link.href = url;
                    link.textContent = linkText;
                    link.target = '_blank';
                    link.rel = 'noopener noreferrer';

                    // Replace selection with link
                    range.deleteContents();
                    range.insertNode(link);

                    // Update form control
                    const content = editorElement.innerHTML;
                    this.latestUpdateForm.get('latestUpdateContent')?.setValue(content);
                }
            }
        }
    }

    private removeCKEditorBranding(): void {
        // Add aggressive CSS to hide all possible CKEditor branding
        const style = document.createElement('style');
        style.textContent = `
            /* Hide all possible CKEditor branding */
            .ck-powered-by,
            .ck-powered-by-label,
            .ck-powered-by-balloon,
            .ck-balloon-panel .ck-powered-by,
            .ck-content .ck-powered-by,
            [class*="powered-by"],
            [class*="ck-powered"],
            .ck.ck-balloon-panel.ck-powered-by-balloon {
                display: none !important;
                visibility: hidden !important;
                opacity: 0 !important;
                height: 0 !important;
                width: 0 !important;
                position: absolute !important;
                top: -9999px !important;
                left: -9999px !important;
            }
            
            /* Hide any branding that appears on focus */
            .ck-content:focus ~ .ck-powered-by,
            .ck-content:focus + .ck-powered-by,
            .ck-editor:focus .ck-powered-by,
            .ck-editor__editable:focus ~ .ck-powered-by {
                display: none !important;
                visibility: hidden !important;
            }
        `;
        document.head.appendChild(style);

        // Also continuously monitor and remove any branding elements
        const removeBrandingInterval = setInterval(() => {
            const brandingElements = document.querySelectorAll(
                '.ck-powered-by, .ck-powered-by-label, .ck-powered-by-balloon, [class*="powered-by"], [class*="ck-powered"]'
            );
            brandingElements.forEach(element => {
                (element as HTMLElement).style.display = 'none';
                (element as HTMLElement).style.visibility = 'hidden';
                element.remove();
            });

            // Also check for any text nodes containing "CKEditor"
            const allElements = document.querySelectorAll('*:not(.ck-content):not(.ck-editor__editable)');
            allElements.forEach(element => {
                if (element.textContent && element.textContent.includes('CKEditor') &&
                    element.children.length === 0) { // Only text nodes, no child elements
                    element.textContent = element.textContent.replace(/CKEditor/g, '');
                }
            });
        }, 500);

        // Clear interval after 3 seconds to avoid permanent overhead
        setTimeout(() => clearInterval(removeBrandingInterval), 3000);
    }
}
